import React, { useCallback, useEffect, useMemo, useState } from "react";
import {
    App,
    Alert,
    Button,
    Empty,
    Form,
    Input,
    Modal,
    Space,
    Table,
    Tag,
    Tooltip,
    Typography,
} from "antd";
import { PlusOutlined, ReloadOutlined } from "@ant-design/icons";
import { useSearchParams } from "react-router-dom";
import dayjs from "dayjs";
import { flushSync } from "react-dom";

import { useAuth } from "../../../hooks/useAuth";
import salesIataFuelPriceApi from "../../../api/salesIataFuelPriceApi";
import FuelPriceDetailDrawer from "./components/FuelPriceDetailDrawer";
import FuelPriceFormModal from "./components/FuelPriceFormModal";
import TaskRunDetailDrawer from "./components/TaskRunDetailDrawer";
import FuelWeeklyTrendChart from "./components/FuelWeeklyTrendChart";

const { Link, Text } = Typography;

const statusTextMap = {
    PENDING_CONFIRM: "待确认",
    CONFIRMED: "已生效",
    REJECTED: "已驳回",
};

const sourceTextMap = {
    MANUAL: "手工录入",
    CRAWLER_AUTO: "自动抓取",
    OCR_AUTO: "图片解析",
    MANUAL_PATCH: "人工修正",
};

const statusBadgeStyleMap = {
    PENDING_CONFIRM: {
        color: "#d46b08",
        background: "#fff7e6",
        border: "1px solid #ffd591",
    },
    CONFIRMED: {
        color: "#389e0d",
        background: "#f6ffed",
        border: "1px solid #b7eb8f",
    },
    REJECTED: {
        color: "#595959",
        background: "#fafafa",
        border: "1px solid #d9d9d9",
    },
};

const sourceBadgeStyleMap = {
    MANUAL: {
        color: "#08979c",
        background: "#e6fffb",
        border: "1px solid #87e8de",
    },
    CRAWLER_AUTO: {
        color: "#1d39c4",
        background: "#f0f5ff",
        border: "1px solid #adc6ff",
    },
    OCR_AUTO: {
        color: "#1d39c4",
        background: "#f0f5ff",
        border: "1px solid #adc6ff",
    },
    MANUAL_PATCH: {
        color: "#595959",
        background: "#fafafa",
        border: "1px solid #d9d9d9",
    },
};

const taskStatusColorMap = {
    RUNNING: "processing",
    REVIEW_REQUIRED: "gold",
    IMPORTED: "green",
    FAILED: "red",
};

const taskStatusTextMap = {
    RUNNING: "运行中",
    REVIEW_REQUIRED: "需人工处理",
    IMPORTED: "已转记录",
    FAILED: "任务失败",
};

const taskTriggerModeTextMap = {
    MANUAL: "手动触发",
    SCHEDULED: "定时触发",
    RETRY: "失败重试",
};

const taskParseStatusTextMap = {
    PARSED: "图片已解析",
    REVIEW_REQUIRED: "解析需人工修正",
    FAILED: "图片解析失败",
    SKIPPED: "未执行图片解析",
};

const renderMoney = (value) => (value == null ? "—" : Number(value).toFixed(2));
const renderDateTime = (value) => {
    if (!value) {
        return "—";
    }
    const parsed = dayjs(value);
    return parsed.isValid() ? parsed.format("YYYY-MM-DD HH:mm:ss") : value;
};
const renderPill = (text, style) => (
    <span
        style={{
            display: "inline-flex",
            alignItems: "center",
            justifyContent: "center",
            minWidth: 72,
            height: 28,
            padding: "0 12px",
            borderRadius: 999,
            fontSize: 13,
            fontWeight: 500,
            ...style,
        }}
    >
        {text}
    </span>
);
const renderStatusBadge = (value) => renderPill(statusTextMap[value] || value, statusBadgeStyleMap[value] || statusBadgeStyleMap.REJECTED);
// 自动确认：已自动确认的行打标签；OCR 待确认行悬浮显示未通过的校验
const renderAutoConfirmHint = (record) => {
    const autoConfirm = record?.auto_confirm;
    if (!autoConfirm) return null;
    if (record.status === "CONFIRMED" && autoConfirm.confirmed) {
        return (
            <Tooltip title="全部自动校验通过，系统自动确认；计费月未出燃油附加费应收前可撤回">
                <Tag color="cyan" style={{ marginInlineEnd: 0 }}>
                    自动确认
                </Tag>
            </Tooltip>
        );
    }
    const failedRules = autoConfirm.failed_rules || [];
    if (record.status !== "PENDING_CONFIRM" || failedRules.length === 0) return null;
    return (
        <Tooltip
            title={
                <div>
                    <div>未自动确认，以下校验未通过：</div>
                    {failedRules.map((rule) => (
                        <div key={rule.code}>
                            · {rule.label}：{rule.reason}
                        </div>
                    ))}
                </div>
            }
        >
            <Tag color="orange" style={{ marginInlineEnd: 0, cursor: "help" }}>
                {failedRules.length} 项未通过
            </Tag>
        </Tooltip>
    );
};
const renderSourceBadge = (value) => renderPill(sourceTextMap[value] || value, sourceBadgeStyleMap[value] || sourceBadgeStyleMap.MANUAL_PATCH);
const getCurrentBillingMonth = () => dayjs().format("YYYY-MM");
const getPreviousMonth = (monthText = getCurrentBillingMonth()) => dayjs(`${monthText}-01`).subtract(1, "month").format("YYYY-MM");
// 趋势图取最近 N 条已生效周记录（list 接口 pageSize 上限 200，约四年周数据）
const TREND_WEEK_LIMIT = 200;
const getTaskDownloadedImages = (task) => (Array.isArray(task?.raw_payload?.downloaded_images) ? task.raw_payload.downloaded_images : []);
const getTaskSnapshotManifest = (task) => task?.raw_payload?.snapshot_manifest || {};
const getTaskChartImage = (task) => {
    const chartImage = getTaskSnapshotManifest(task)?.chart_image;
    if (chartImage) {
        return chartImage;
    }
    const downloadedImages = getTaskDownloadedImages(task);
    return (
        downloadedImages.find((image) =>
            String(image?.absolute_src || image?.src || image?.url || "")
                .toLowerCase()
                .includes("fuel_price_analysis")
        ) ||
        downloadedImages[0] ||
        null
    );
};
const getImageDisplayName = (image) => {
    if (!image) {
        return "";
    }
    const candidates = [image.filename, image.object_key, image.src, image.absolute_src, image.url].filter(Boolean);
    for (const candidate of candidates) {
        const clean = String(candidate).split("?")[0].split("#")[0];
        const name = clean.split("/").filter(Boolean).pop();
        if (name) {
            return name;
        }
    }
    return "查看原图";
};
const getImageViewUrl = (image) => image?.url || image?.absolute_src || image?.src || "";
const getTaskChartParse = (task) => task?.raw_payload?.chart_parse || {};
const getTaskChartNormalized = (task) => getTaskChartParse(task)?.normalized || {};
const getTaskSourceWeekMismatch = (task) => getTaskChartParse(task)?.source_week_mismatch || null;

const SalesIataFuelPricePage = () => {
    const { message: messageApi, modal } = App.useApp();
    const { hasPermission } = useAuth();
    const [searchParams, setSearchParams] = useSearchParams();
    const [listData, setListData] = useState({ items: [], total: 0, page: 1, page_size: 20 });
    const [taskData, setTaskData] = useState({ items: [], total: 0, page: 1, page_size: 10 });
    const [loading, setLoading] = useState(false);
    const [taskLoading, setTaskLoading] = useState(false);
    const [summaryLoading, setSummaryLoading] = useState(false);
    const [latestConfirmed, setLatestConfirmed] = useState(null);
    const [pendingCount, setPendingCount] = useState(0);
    const [billingReference, setBillingReference] = useState(null);
    const [currentMonthSummary, setCurrentMonthSummary] = useState(null);
    const [trendRecords, setTrendRecords] = useState([]);
    const [detailOpen, setDetailOpen] = useState(false);
    const [detailLoading, setDetailLoading] = useState(false);
    const [detailRecord, setDetailRecord] = useState(null);
    const [detailLogs, setDetailLogs] = useState([]);
    const [detailLogsLoading, setDetailLogsLoading] = useState(false);
    const [detailOriginalPageHtml, setDetailOriginalPageHtml] = useState("");
    const [detailOriginalPageLoading, setDetailOriginalPageLoading] = useState(false);
    const [formOpen, setFormOpen] = useState(false);
    const [editingRecord, setEditingRecord] = useState(null);
    const [draftTaskRunId, setDraftTaskRunId] = useState(null);
    const [formSubmitting, setFormSubmitting] = useState(false);
    const [rejectModalOpen, setRejectModalOpen] = useState(false);
    const [rejectingRecord, setRejectingRecord] = useState(null);
    const [rejectSubmitting, setRejectSubmitting] = useState(false);
    const [taskPage, setTaskPage] = useState(1);
    const [taskPageSize, setTaskPageSize] = useState(10);
    const [taskStatus, setTaskStatus] = useState("");
    const [taskDetailOpen, setTaskDetailOpen] = useState(false);
    const [taskDetailLoading, setTaskDetailLoading] = useState(false);
    const [taskDetailRecord, setTaskDetailRecord] = useState(null);
    const [rejectForm] = Form.useForm();
    const activeTab = ["table", "trend", "tasks"].includes(searchParams.get("tab") || "") ? searchParams.get("tab") || "table" : "table";
    const month = searchParams.get("month") || "";
    const status = searchParams.get("status") || "";
    const page = Number(searchParams.get("page") || "1");
    const pageSize = Number(searchParams.get("pageSize") || "20");
    const canCreate = hasPermission("sales:fuel-price:create");
    const canUpdate = hasPermission("sales:fuel-price:update");
    const canConfirm = hasPermission("sales:fuel-price:confirm");

    const updateQuery = (patch) => {
        const next = new URLSearchParams(searchParams);
        Object.entries(patch).forEach(([key, value]) => {
            if (value == null || value === "" || value === false) {
                next.delete(key);
            } else {
                next.set(key, String(value));
            }
        });
        setSearchParams(next, { replace: true });
    };

    const resetFormState = () => {
        setFormOpen(false);
        setEditingRecord(null);
        setDraftTaskRunId(null);
    };

    const loadList = useCallback(async () => {
        setLoading(true);
        try {
            const response = await salesIataFuelPriceApi.getList({
                page,
                pageSize,
                month: month || undefined,
                status: status || undefined,
            });
            setListData(response.data);
        } catch (error) {
            messageApi.error(error.response?.data?.detail || "加载 IATA 油价列表失败");
            setListData({ items: [], total: 0, page, page_size: pageSize });
        } finally {
            setLoading(false);
        }
    }, [messageApi, month, page, pageSize, status]);

    const loadTaskRuns = useCallback(async () => {
        setTaskLoading(true);
        try {
            const response = await salesIataFuelPriceApi.getTaskRuns({
                page: taskPage,
                pageSize: taskPageSize,
                status: taskStatus || undefined,
            });
            setTaskData(response.data);
        } catch (error) {
            messageApi.error(error.response?.data?.detail || "加载抓取任务失败");
            setTaskData({ items: [], total: 0, page: taskPage, page_size: taskPageSize });
        } finally {
            setTaskLoading(false);
        }
    }, [messageApi, taskPage, taskPageSize, taskStatus]);

    const loadSummary = useCallback(async () => {
        setSummaryLoading(true);
        try {
            const billingMonth = getCurrentBillingMonth();
            const [confirmedResp, pendingResp, billingResp, monthSummaryResp, trendResp] = await Promise.all([
                salesIataFuelPriceApi.getList({ page: 1, pageSize: 1, status: "CONFIRMED" }),
                salesIataFuelPriceApi.getList({ page: 1, pageSize: 1, status: "PENDING_CONFIRM" }),
                salesIataFuelPriceApi.getBillingReference(billingMonth),
                salesIataFuelPriceApi.getMonthSummary(month || getPreviousMonth()),
                salesIataFuelPriceApi.getList({ page: 1, pageSize: TREND_WEEK_LIMIT, status: "CONFIRMED" }),
            ]);
            setLatestConfirmed(confirmedResp.data.items?.[0] || null);
            setPendingCount(pendingResp.data.total || 0);
            setBillingReference(billingResp.data);
            setCurrentMonthSummary(monthSummaryResp.data);
            setTrendRecords(trendResp.data?.items || []);
        } catch (error) {
            messageApi.error(error.response?.data?.detail || "加载油价摘要失败");
        } finally {
            setSummaryLoading(false);
        }
    }, [messageApi, month]);

    useEffect(() => {
        loadList();
    }, [loadList]);

    useEffect(() => {
        loadSummary();
    }, [loadSummary]);

    useEffect(() => {
        loadTaskRuns();
    }, [loadTaskRuns]);

    const reloadAll = async () => {
        await Promise.all([loadList(), loadSummary(), loadTaskRuns()]);
    };

    const openDetail = async (recordId) => {
        setDetailOpen(true);
        setDetailLoading(true);
        setDetailLogsLoading(true);
        setDetailOriginalPageLoading(true);
        try {
            const [detailResponse, logsResponse, originalPageResponse] = await Promise.allSettled([
                salesIataFuelPriceApi.getById(recordId),
                salesIataFuelPriceApi.getLogs(recordId, { page: 1, pageSize: 50 }),
                salesIataFuelPriceApi.getOriginalPage(recordId),
            ]);
            if (detailResponse.status !== "fulfilled") {
                throw detailResponse.reason;
            }
            setDetailRecord(detailResponse.value.data);
            setDetailLogs(logsResponse.status === "fulfilled" ? logsResponse.value.data.items || [] : []);
            setDetailOriginalPageHtml(originalPageResponse.status === "fulfilled" ? originalPageResponse.value.data.html || "" : "");
        } catch (error) {
            messageApi.error(error.response?.data?.detail || "加载详情失败");
            setDetailRecord(null);
            setDetailLogs([]);
            setDetailOriginalPageHtml("");
        } finally {
            setDetailLoading(false);
            setDetailLogsLoading(false);
            setDetailOriginalPageLoading(false);
        }
    };

    const openEdit = async (recordId, options = {}) => {
        setDetailLoading(true);
        try {
            const response = await salesIataFuelPriceApi.getById(recordId);
            if (options.requirePending && response.data.status !== "PENDING_CONFIRM") {
                messageApi.warning("只有待确认草稿可以修改");
                return;
            }
            setDraftTaskRunId(null);
            setEditingRecord(response.data);
            setFormOpen(true);
        } catch (error) {
            messageApi.error(error.response?.data?.detail || "加载编辑数据失败");
        } finally {
            setDetailLoading(false);
        }
    };

    const openTaskAsDraft = async (taskRunId) => {
        try {
            const response = await salesIataFuelPriceApi.getTaskRun(taskRunId);
            const task = response.data;
            const sourceWeekMismatch = getTaskSourceWeekMismatch(task);
            if (sourceWeekMismatch) {
                messageApi.warning("源图表周结束日与目标周结束日不一致，请等待补偿抓取或重试抓取");
                return;
            }
            const normalizedChart = getTaskChartNormalized(task);
            const isManualCorrection = task.status === "REVIEW_REQUIRED" || task.parsed_global_price_per_mt == null;
            setEditingRecord({
                week_end_date: task.candidate_week_end_date,
                publish_date: task.publish_date || task.source_captured_at,
                global_price_per_mt: task.parsed_global_price_per_mt,
                global_price_per_bbl: task.parsed_global_price_per_bbl,
                north_america_price_per_mt: normalizedChart.north_america_price_per_mt,
                europe_price_per_mt: normalizedChart.europe_price_per_mt ?? normalizedChart.europe_cis_price_per_mt,
                asia_oceania_price_per_mt: normalizedChart.asia_oceania_price_per_mt,
                source_type: isManualCorrection ? "MANUAL_PATCH" : "OCR_AUTO",
                status: "PENDING_CONFIRM",
                source_url: task.target_url,
                source_captured_at: task.source_captured_at,
                layout_column_middle_html: task.layout_column_middle_html,
                layout_column_middle_text: task.layout_column_middle_text,
                raw_payload: task.raw_payload,
                parse_confidence: task.parse_confidence,
                remark: `${isManualCorrection ? "人工修正自" : "转录自"}抓取任务 #${task.id}${task.result_summary ? ` · ${task.result_summary}` : ""}`,
            });
            setDraftTaskRunId(task.id);
            setFormOpen(true);
        } catch (error) {
            messageApi.error(error.response?.data?.detail || "加载抓取任务详情失败");
        }
    };

    const openTaskDetail = async (taskRunId) => {
        setTaskDetailOpen(true);
        setTaskDetailLoading(true);
        try {
            const response = await salesIataFuelPriceApi.getTaskRun(taskRunId);
            setTaskDetailRecord(response.data);
        } catch (error) {
            messageApi.error(error.response?.data?.detail || "加载抓取任务详情失败");
            setTaskDetailRecord(null);
        } finally {
            setTaskDetailLoading(false);
        }
    };

    const refreshTaskDetailIfOpen = async (taskRunId) => {
        if (taskDetailOpen && taskDetailRecord?.id === taskRunId) {
            await openTaskDetail(taskRunId);
        }
    };

    const handleFormSubmit = async (values) => {
        setFormSubmitting(true);
        try {
            if (editingRecord?.id) {
                await salesIataFuelPriceApi.update({ id: editingRecord.id, data: values });
                messageApi.success("IATA 油价记录更新成功");
            } else {
                const createResponse = await salesIataFuelPriceApi.create(values);
                const createdId = createResponse.data?.data?.id;
                let linkError = null;
                if (draftTaskRunId && createdId) {
                    try {
                        await salesIataFuelPriceApi.linkTaskRunRecord(draftTaskRunId, createdId);
                    } catch (error) {
                        linkError = error;
                    }
                }
                if (linkError) {
                    const detail = linkError.response?.data?.detail;
                    messageApi.warning(detail ? `记录已创建，但任务关联失败：${detail}` : "记录已创建，但任务关联失败，请稍后重试");
                } else {
                    messageApi.success(draftTaskRunId ? "抓取任务已转为待确认草稿" : "IATA 油价记录创建成功");
                }
            }
            flushSync(() => {
                resetFormState();
            });
            await reloadAll();
        } catch (error) {
            messageApi.error(error.response?.data?.detail || "保存 IATA 油价记录失败");
        } finally {
            setFormSubmitting(false);
        }
    };

    const handleConfirm = async (record) => {
        try {
            await salesIataFuelPriceApi.confirm(record.id, {});
            messageApi.success(`已确认生效：${record.week_end_date}`);
            await reloadAll();
            if (detailRecord?.id === record.id) {
                await openDetail(record.id);
            }
        } catch (error) {
            messageApi.error(error.response?.data?.detail || "确认生效失败");
        }
    };

    const handleReject = async () => {
        try {
            const values = await rejectForm.validateFields();
            setRejectSubmitting(true);
            await salesIataFuelPriceApi.reject(rejectingRecord.id, { reason: values.reason });
            messageApi.success(`已驳回：${rejectingRecord.week_end_date}`);
            setRejectModalOpen(false);
            setRejectingRecord(null);
            rejectForm.resetFields();
            await reloadAll();
        } catch (error) {
            if (error?.errorFields) {
                return;
            }
            messageApi.error(error.response?.data?.detail || "驳回失败");
        } finally {
            setRejectSubmitting(false);
        }
    };

    const handleReopen = (record) => {
        const isAutoConfirmed = record.status === "CONFIRMED";
        modal.confirm({
            title: `${isAutoConfirmed ? "撤回自动确认" : "恢复待确认"} - ${record.week_end_date}`,
            content: isAutoConfirmed
                ? "撤回后这条记录回到待确认状态，之后只能人工确认；对应计费月已生成燃油附加费应收时不能撤回。"
                : "恢复后这条记录会回到待确认状态，可继续修改草稿、确认生效或再次驳回。",
            okText: isAutoConfirmed ? "撤回自动确认" : "恢复待确认",
            cancelText: "取消",
            async onOk() {
                const response = await salesIataFuelPriceApi.batchReopen(
                    [record.id],
                    isAutoConfirmed ? "撤回自动确认的 IATA 油价记录" : "恢复已驳回 IATA 油价记录为待确认",
                );
                const skipped = response?.data?.data?.skipped || response?.data?.skipped || [];
                if (skipped.length > 0) {
                    messageApi.warning(
                        skipped[0].reason === "AUTO_CONFIRMED_REFERENCED"
                            ? "对应计费月已生成燃油附加费应收，不能撤回"
                            : "这条记录不能恢复为待确认",
                    );
                    return;
                }
                messageApi.success(`已恢复待确认：${record.week_end_date}`);
                await reloadAll();
                if (detailRecord?.id === record.id) {
                    await openDetail(record.id);
                }
            },
        });
    };

    const handleRetryTask = async (taskRunId) => {
        try {
            const response = await salesIataFuelPriceApi.retryTaskRun(taskRunId);
            const success = response.data?.success !== false;
            messageApi[success ? "success" : "warning"](response.data?.message || "重试抓取已完成");
            await reloadAll();
            await refreshTaskDetailIfOpen(taskRunId);
        } catch (error) {
            messageApi.error(error.response?.data?.detail || "重试抓取失败");
        }
    };

    const handleParseTask = async (taskRunId) => {
        try {
            const response = await salesIataFuelPriceApi.parseTaskRunChart(taskRunId);
            const success = response.data?.success !== false;
            messageApi[success ? "success" : "warning"](response.data?.message || "图片解析已完成");
            await reloadAll();
            await refreshTaskDetailIfOpen(taskRunId);
        } catch (error) {
            messageApi.error(error.response?.data?.detail || "图片解析失败");
        }
    };

    const monthOptions = useMemo(() => {
        return Array.from(
            new Set(
                [
                    ...listData.items.map((item) => item.week_end_date?.slice(0, 7)),
                    month || null,
                    currentMonthSummary?.month,
                    billingReference?.reference_month,
                    getPreviousMonth(),
                ].filter(Boolean),
            ),
        ).sort((left, right) => right.localeCompare(left));
    }, [billingReference?.reference_month, currentMonthSummary?.month, listData.items, month]);

    const confirmedRows = useMemo(() => {
        return listData.items.filter((item) => item.status === "CONFIRMED" && item.global_price_per_mt != null);
    }, [listData.items]);

    const filteredAveragePrice = useMemo(() => {
        if (!confirmedRows.length) {
            return null;
        }
        const totalPrice = confirmedRows.reduce((sum, item) => sum + Number(item.global_price_per_mt), 0);
        return totalPrice / confirmedRows.length;
    }, [confirmedRows]);

    const pendingRowsCount = useMemo(() => {
        return listData.items.filter((item) => item.status === "PENDING_CONFIRM").length;
    }, [listData.items]);

    const tableTitle = month ? `${month} 油价周数据` : "IATA 油价周数据";

    const columns = [
        {
            title: <div style={{ textAlign: "center" }}>周结束日期</div>,
            dataIndex: "week_end_date",
            key: "week_end_date",
            align: "center",
        },
        {
            title: <div style={{ textAlign: "center" }}>Global $/MT</div>,
            dataIndex: "global_price_per_mt",
            key: "global_price_per_mt",
            align: "center",
            render: (value) => renderMoney(value),
        },
        {
            title: <div style={{ textAlign: "center" }}>状态</div>,
            dataIndex: "status",
            key: "status",
            align: "center",
            render: (value, record) => {
                const hint = renderAutoConfirmHint(record);
                if (!hint) return renderStatusBadge(value);
                return (
                    <Space size={4} direction="vertical" align="center">
                        {renderStatusBadge(value)}
                        {hint}
                    </Space>
                );
            },
        },
        {
            title: <div style={{ textAlign: "center" }}>来源</div>,
            dataIndex: "source_type",
            key: "source_type",
            align: "center",
            render: (value) => renderSourceBadge(value),
        },
        {
            title: <div style={{ textAlign: "center" }}>录入时间</div>,
            dataIndex: "created_at",
            key: "created_at",
            align: "center",
            render: (value) => renderDateTime(value),
        },
        {
            title: <div style={{ textAlign: "center" }}>操作</div>,
            key: "actions",
            align: "center",
            render: (_, record) => (
                <div style={{ display: "flex", justifyContent: "center", alignItems: "center", gap: 8, flexWrap: "wrap" }}>
                    <Button type="link" size="small" onClick={() => openDetail(record.id)}>
                        查看详情
                    </Button>
                    {record.status === "PENDING_CONFIRM" && canUpdate ? (
                        <Button type="link" size="small" onClick={() => openEdit(record.id)}>
                            修改草稿
                        </Button>
                    ) : null}
                    {record.status === "PENDING_CONFIRM" && canConfirm ? (
                        <>
                            <Button type="link" size="small" onClick={() => handleConfirm(record)}>
                                确认生效
                            </Button>
                            <Button
                                type="link"
                                size="small"
                                danger
                                onClick={() => {
                                    setRejectingRecord(record);
                                    setRejectModalOpen(true);
                                }}
                            >
                                驳回
                            </Button>
                        </>
                    ) : null}
                    {record.status === "REJECTED" && canConfirm ? (
                        <Button type="link" size="small" onClick={() => handleReopen(record)}>
                            恢复待确认
                        </Button>
                    ) : null}
                    {record.status === "CONFIRMED" && record.auto_confirm?.confirmed && canConfirm ? (
                        <Button type="link" size="small" onClick={() => handleReopen(record)}>
                            撤回自动确认
                        </Button>
                    ) : null}
                </div>
            ),
        },
    ];

    const taskColumns = [
        {
            title: "任务状态",
            dataIndex: "status",
            key: "status",
            render: (value) => <Tag color={taskStatusColorMap[value]}>{taskStatusTextMap[value] || value}</Tag>,
        },
        {
            title: "触发方式",
            dataIndex: "trigger_mode",
            key: "trigger_mode",
            render: (value) => taskTriggerModeTextMap[value] || value,
        },
        {
            title: "抓取时间",
            dataIndex: "source_captured_at",
            key: "source_captured_at",
            render: (value) => renderDateTime(value),
        },
        {
            title: "目标周结束日",
            dataIndex: "candidate_week_end_date",
            key: "candidate_week_end_date",
            render: (value, record) => {
                const normalizedChart = getTaskChartNormalized(record);
                const sourceWeekEndDate = normalizedChart.week_end_date;
                const mismatch = getTaskSourceWeekMismatch(record);
                return (
                    <div style={{ display: "grid", gap: 4 }}>
                        <Text>{value || "—"}</Text>
                        {sourceWeekEndDate && sourceWeekEndDate !== value ? (
                            <Text type={mismatch ? "warning" : "secondary"}>源图：{sourceWeekEndDate}</Text>
                        ) : null}
                    </div>
                );
            },
        },
        {
            title: "解析摘要",
            key: "summary",
            render: (_, record) => {
                const chartParse = getTaskChartParse(record);
                const metricText = [];
                if (record.parsed_global_price_per_mt != null) {
                    metricText.push(`$${Number(record.parsed_global_price_per_mt).toFixed(2)}/MT`);
                }
                if (record.parsed_global_price_per_bbl != null) {
                    metricText.push(`$${Number(record.parsed_global_price_per_bbl).toFixed(2)}/bbl`);
                }
                return (
                    <div style={{ display: "grid", gap: 4 }}>
                        <Text>{metricText.length ? metricText.join(" · ") : "尚未读出图表价格"}</Text>
                        <Text type="secondary">
                            {taskParseStatusTextMap[chartParse.status] || "等待图片解析"}
                            {record.parse_confidence != null ? ` · 置信度 ${Number(record.parse_confidence).toFixed(0)}` : ""}
                        </Text>
                        <Text type="secondary" ellipsis={{ tooltip: record.result_summary || record.error_message || "—" }}>
                            {record.result_summary || record.error_message || "—"}
                        </Text>
                    </div>
                );
            },
        },
        {
            title: "证据留档",
            key: "artifacts",
            render: (_, record) => {
                const downloadedImages = getTaskDownloadedImages(record);
                const chartImage = getTaskChartImage(record);
                const chartImageName = getImageDisplayName(chartImage);
                const chartImageUrl = getImageViewUrl(chartImage);
                const imageEvidenceText = record.screenshot_path
                    ? "截图已保存"
                    : downloadedImages.length
                        ? `图片已留档 ${downloadedImages.length} 张`
                        : "无图片";
                return (
                    <div style={{ display: "grid", gap: 4 }}>
                        <Text code>{imageEvidenceText}</Text>
                        {chartImage ? (
                            chartImageUrl ? (
                                <Link
                                    href={chartImageUrl}
                                    target="_blank"
                                    rel="noreferrer"
                                    title={chartImageUrl}
                                    ellipsis
                                    style={{ maxWidth: 240 }}
                                >
                                    {chartImageName || "查看原图"}
                                </Link>
                            ) : (
                                <Text type="secondary" ellipsis={{ tooltip: chartImageName }}>
                                    {chartImageName}
                                </Text>
                            )
                        ) : null}
                        <Text code>{record.page_html_path ? "HTML 已留档" : "无 HTML"}</Text>
                    </div>
                );
            },
        },
        {
            title: "操作",
            key: "actions",
            render: (_, record) => (
                <Space size="small" wrap>
                    <Button type="link" size="small" onClick={() => openTaskDetail(record.id)}>
                        查看详情
                    </Button>
                    {record.fuel_price_id ? (
                        <Button type="link" size="small" onClick={() => openDetail(record.fuel_price_id)}>
                            查看关联记录
                        </Button>
                    ) : null}
                    {record.fuel_price_id && record.fuel_price_status === "PENDING_CONFIRM" && canUpdate ? (
                        <Button type="link" size="small" onClick={() => openEdit(record.fuel_price_id, { requirePending: true })}>
                            修改关联草稿
                        </Button>
                    ) : null}
                    {record.status !== "RUNNING" && record.status !== "IMPORTED" && canCreate ? (
                        <Button type="link" size="small" onClick={() => handleParseTask(record.id)}>
                            重新解析图片
                        </Button>
                    ) : null}
                    {record.status === "REVIEW_REQUIRED" && !record.fuel_price_id && !getTaskSourceWeekMismatch(record) && canCreate ? (
                        <Button type="link" size="small" onClick={() => openTaskAsDraft(record.id)}>
                            人工修正入库
                        </Button>
                    ) : null}
                    {record.status !== "RUNNING" && canCreate ? (
                        <Button type="link" size="small" icon={<ReloadOutlined />} onClick={() => handleRetryTask(record.id)}>
                            重试抓取
                        </Button>
                    ) : null}
                </Space>
            ),
        },
    ];

    return (
        <div className="page-container">
            <div className="page-header-compact">
                <div className="page-title">
                    <h2>IATA 油价管理</h2>
                    <span className="page-description">管理每周 IATA Jet Fuel Price 数据，生成后续燃油调整的计费基准</span>
                </div>
                <Space wrap>
                    {canCreate ? (
                        <Button
                            type="primary"
                            icon={<PlusOutlined />}
                            onClick={() => {
                                setDraftTaskRunId(null);
                                setEditingRecord(null);
                                setFormOpen(true);
                            }}
                        >
                            录入周油价
                        </Button>
                    ) : null}
                </Space>
            </div>

            <Alert
                type="info"
                showIcon
                style={{ marginBottom: 16 }}
                message="列表仅展示关键字段（周、Global $/MT、状态）。解析入库仍保留完整字段（各区域、置信度、原文、快照）供后续扩展使用。"
            />

            <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(220px, 1fr))", gap: 16, marginBottom: 16 }}>
                <div style={{ border: "1px solid #f0f0f0", borderRadius: 8, background: "#fff", padding: "16px 20px" }}>
                    <div style={{ color: "rgba(0,0,0,0.45)", fontSize: 13, marginBottom: 12 }}>最新生效油价 ($/MT)</div>
                    <div style={{ color: "#1677ff", fontSize: 32, fontWeight: 600, lineHeight: 1.2 }}>
                        {summaryLoading ? "..." : latestConfirmed ? Number(latestConfirmed.global_price_per_mt).toFixed(2) : "—"}
                    </div>
                </div>
                <div style={{ border: "1px solid #f0f0f0", borderRadius: 8, background: "#fff", padding: "16px 20px" }}>
                    <div style={{ color: "rgba(0,0,0,0.45)", fontSize: 13, marginBottom: 12 }}>最新生效周期</div>
                    <div style={{ color: "#262626", fontSize: 20, fontWeight: 500, lineHeight: 1.2 }}>{summaryLoading ? "..." : latestConfirmed?.week_end_date || "—"}</div>
                </div>
                <div style={{ border: "1px solid #f0f0f0", borderRadius: 8, background: "#fff", padding: "16px 20px" }}>
                    <div style={{ color: "rgba(0,0,0,0.45)", fontSize: 13, marginBottom: 12 }}>
                        {billingReference?.reference_month || getPreviousMonth()} 计费基准 ($/MT)
                    </div>
                    <div style={{ color: billingReference?.is_ready ? "#faad14" : "#ff4d4f", fontSize: 32, fontWeight: 600, lineHeight: 1.2 }}>
                        {summaryLoading ? "..." : billingReference?.average_price_per_mt != null ? Number(billingReference.average_price_per_mt).toFixed(2) : "—"}
                    </div>
                    <div style={{ marginTop: 8, color: billingReference?.is_ready ? "rgba(0,0,0,0.45)" : "#ff4d4f", fontSize: 12 }}>
                        {billingReference?.is_ready
                            ? `${billingReference.confirmed_weeks}/${billingReference.expected_weeks} 周已生效`
                            : `缺 ${billingReference?.missing_week_end_dates?.length || 0} 周，暂不可计费`}
                    </div>
                </div>
                <div
                    style={{ border: "1px solid #f0f0f0", borderRadius: 8, background: "#fff", padding: "16px 20px", cursor: "pointer" }}
                    onClick={() => updateQuery({ status: "PENDING_CONFIRM", page: 1, tab: "table" })}
                >
                    <div style={{ color: "rgba(0,0,0,0.45)", fontSize: 13, marginBottom: 12 }}>待确认解析记录</div>
                    <div style={{ color: "#ff4d4f", fontSize: 32, fontWeight: 600, lineHeight: 1.2 }}>{summaryLoading ? "..." : pendingCount}</div>
                </div>
            </div>

            <div style={{ display: "flex", alignItems: "center", borderBottom: "1px solid #f0f0f0", marginBottom: 16, gap: 8 }}>
                {[
                    { key: "table", label: "周数据表" },
                    { key: "trend", label: "周度趋势" },
                    { key: "tasks", label: "自动抓取任务" },
                ].map((item) => {
                    const isActive = activeTab === item.key;
                    return (
                        <button
                            key={item.key}
                            type="button"
                            onClick={() => updateQuery({ tab: item.key })}
                            style={{
                                border: "none",
                                borderBottom: isActive ? "2px solid #1677ff" : "2px solid transparent",
                                background: "transparent",
                                color: isActive ? "#1677ff" : "#262626",
                                padding: "0 0 12px",
                                marginRight: 32,
                                fontSize: 16,
                                fontWeight: 500,
                                cursor: "pointer",
                            }}
                        >
                            {item.label}
                        </button>
                    );
                })}
            </div>

            {activeTab === "table" ? (
                <div style={{ border: "1px solid #f0f0f0", borderRadius: 8, background: "#fff", overflow: "hidden" }}>
                    <div
                        style={{
                            display: "flex",
                            justifyContent: "space-between",
                            alignItems: "center",
                            gap: 12,
                            flexWrap: "wrap",
                            padding: "16px 20px",
                            borderBottom: "1px solid #f0f0f0",
                        }}
                    >
                        <span style={{ color: "#262626", fontSize: 15, fontWeight: 500 }}>{tableTitle}</span>
                        <Space wrap>
                            <select
                                value={month}
                                onChange={(event) => updateQuery({ month: event.target.value, page: 1 })}
                                style={{ height: 32, minWidth: 140, borderRadius: 6, border: "1px solid #d9d9d9", padding: "0 8px" }}
                            >
                                <option value="">全部月份</option>
                                {monthOptions.map((option) => (
                                    <option key={option} value={option}>
                                        {option}
                                    </option>
                                ))}
                            </select>
                            <select
                                value={status}
                                onChange={(event) => updateQuery({ status: event.target.value, page: 1 })}
                                style={{ height: 32, minWidth: 120, borderRadius: 6, border: "1px solid #d9d9d9", padding: "0 8px" }}
                            >
                                <option value="">全部状态</option>
                                <option value="PENDING_CONFIRM">待确认</option>
                                <option value="CONFIRMED">已生效</option>
                                <option value="REJECTED">已驳回</option>
                            </select>
                            <Button onClick={() => updateQuery({ month: null, status: null, page: 1, pageSize: 20 })}>重置</Button>
                        </Space>
                    </div>

                    <Table
                        rowKey="id"
                        loading={loading}
                        columns={columns}
                        dataSource={listData.items}
                        pagination={{
                            current: page,
                            pageSize,
                            total: listData.total,
                            onChange: (nextPage, nextPageSize) => updateQuery({ page: nextPage, pageSize: nextPageSize }),
                        }}
                        locale={{ emptyText: <Empty description={month || status ? "当前筛选条件下暂无记录" : "暂无 IATA 油价记录"} /> }}
                        summary={() => (
                            <Table.Summary.Row style={{ background: "#fafafa" }}>
                                <Table.Summary.Cell index={0} align="center">
                                    筛选汇总
                                </Table.Summary.Cell>
                                <Table.Summary.Cell index={1} align="center">
                                    {renderMoney(filteredAveragePrice)}
                                </Table.Summary.Cell>
                                <Table.Summary.Cell index={2} colSpan={4} align="center">
                                    已生效 {confirmedRows.length} 周，待确认 {pendingRowsCount} 周
                                </Table.Summary.Cell>
                            </Table.Summary.Row>
                        )}
                    />
                </div>
            ) : null}

            {activeTab === "trend" ? (
                <FuelWeeklyTrendChart
                    records={trendRecords}
                    loading={summaryLoading}
                    onSelectMonth={(selectedMonth) => updateQuery({ tab: "table", month: selectedMonth, page: 1 })}
                    footer={
                        <>
                            每个点为一周已生效的 Global $/MT（最近 {TREND_WEEK_LIMIT} 周内），点击可查看该月周数据
                            {billingReference?.reference_month
                                ? `；当前计费基准月份：${billingReference.reference_month}，基准油价 ${renderMoney(billingReference.average_price_per_mt)}`
                                : ""}
                        </>
                    }
                />
            ) : null}

            {activeTab === "tasks" ? (
                <div style={{ border: "1px solid #f0f0f0", borderRadius: 8, background: "#fff", padding: 20 }}>
                    <Alert
                        type="warning"
                        showIcon
                        style={{ marginBottom: 16 }}
                        message="自动抓取会留存 IATA 页面 HTML 与图文证据，解析 fuel_price_analysis 图片后自动生成待确认草稿。若同周记录已驳回或自动处理受阻，可在任务中人工处理。"
                    />
                    <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 16, gap: 12, flexWrap: "wrap" }}>
                        <Space wrap>
                            <select
                                value={taskStatus}
                                onChange={(event) => {
                                    setTaskStatus(event.target.value);
                                    setTaskPage(1);
                                }}
                                style={{ height: 32, minWidth: 140, borderRadius: 6, border: "1px solid #d9d9d9", padding: "0 8px" }}
                            >
                                <option value="">全部任务状态</option>
                                <option value="REVIEW_REQUIRED">需人工处理</option>
                                <option value="IMPORTED">已转记录</option>
                                <option value="FAILED">任务失败</option>
                            </select>
                            <Button icon={<ReloadOutlined />} onClick={() => loadTaskRuns()}>
                                刷新任务
                            </Button>
                        </Space>
                        <Text type="secondary">任务抓取留档默认保存在本机临时目录，供人工核对与追溯。</Text>
                    </div>
                    <Table
                        rowKey="id"
                        loading={taskLoading}
                        columns={taskColumns}
                        dataSource={taskData.items}
                        pagination={{
                            current: taskPage,
                            pageSize: taskPageSize,
                            total: taskData.total,
                            onChange: (nextPage, nextPageSize) => {
                                setTaskPage(nextPage);
                                setTaskPageSize(nextPageSize);
                            },
                        }}
                        locale={{ emptyText: <Empty description="暂无抓取任务" /> }}
                    />
                </div>
            ) : null}

            <FuelPriceFormModal
                open={formOpen}
                initialValues={editingRecord}
                onCancel={resetFormState}
                onSubmit={handleFormSubmit}
                submitting={formSubmitting}
            />

            <FuelPriceDetailDrawer
                open={detailOpen}
                record={detailRecord}
                loading={detailLoading}
                logs={detailLogs}
                logsLoading={detailLogsLoading}
                originalPageHtml={detailOriginalPageHtml}
                originalPageLoading={detailOriginalPageLoading}
                onClose={() => {
                    setDetailOpen(false);
                    setDetailRecord(null);
                    setDetailLogs([]);
                    setDetailOriginalPageHtml("");
                }}
            />

            <TaskRunDetailDrawer
                open={taskDetailOpen}
                taskRun={taskDetailRecord}
                loading={taskDetailLoading}
                canCreate={canCreate}
                onParse={handleParseTask}
                onRetry={handleRetryTask}
                onCreateDraft={openTaskAsDraft}
                onClose={() => {
                    setTaskDetailOpen(false);
                    setTaskDetailRecord(null);
                }}
            />

            <Modal
                title={rejectingRecord ? `驳回记录 - ${rejectingRecord.week_end_date}` : "驳回记录"}
                open={rejectModalOpen}
                onOk={handleReject}
                onCancel={() => {
                    setRejectModalOpen(false);
                    setRejectingRecord(null);
                    rejectForm.resetFields();
                }}
                confirmLoading={rejectSubmitting}
                destroyOnHidden
                forceRender
            >
                <Form form={rejectForm} layout="vertical">
                    <Form.Item name="reason" label="驳回原因" rules={[{ required: true, message: "请输入驳回原因" }]}>
                        <Input.TextArea rows={4} placeholder="例如：HTML 片段与录入数值不一致" />
                    </Form.Item>
                </Form>
            </Modal>
        </div>
    );
};

export default SalesIataFuelPricePage;
